/**
 * One deposit, and the decision about it.
 *
 * Everything §7.2 built exists to reach a human on this screen: the receipt
 * image, whether that image has backed earlier claims, and whether a second
 * approver is still required. A control whose signal never gets rendered is a
 * control nobody acts on.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, apiUrl } from '../api/client.ts';
import { can } from '../auth.ts';
import { at, etb, parseAmount, since } from '../format.ts';
import { href } from '../router.ts';
import {
  Banner,
  ConfirmButton,
  ErrorBanner,
  Field,
  Id,
  ReasonForm,
  Row,
  Spinner,
  Status,
} from '../components/ui.tsx';
import type { AdminDepositDetailView, AdminView, AuditEntry } from '../api/types.ts';

export function DepositDetail({ id, me }: { id: string; me: AdminView }) {
  const [deposit, setDeposit] = useState<AdminDepositDetailView | null>(null);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  /** The amount an admin verified by hand, for a deposit no SMS confirms. */
  const [verified, setVerified] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      setDeposit(await api.deposit(id));
    } catch (err) {
      setError(err);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // The trail is a secondary read and a role may hold `deposit.review`
    // without `audit.read`, so a 403 here must not blank the screen the
    // reviewer came for. Reloaded when the status changes so a just-made
    // decision (or the review claim below) appears in it.
    if (!can(me.role, 'audit.read')) return;
    if (!deposit) return;
    void api
      .audit('deposit', deposit.id)
      .then(({ entries }) => setAudit(entries))
      .catch(() => setAudit(null));
  }, [deposit?.id, deposit?.status, me.role]);

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!deposit) {
    return (
      <section>
        <ErrorBanner error={error} />
        {!error ? <Spinner /> : null}
      </section>
    );
  }

  const decided = deposit.status === 'approved' || deposit.status === 'rejected';
  const approvals = deposit.approvals;
  const iApproved = approvals.some((a) => a.adminId === me.id);
  const needsAnother = approvals.length > 0 && approvals.length < deposit.approvalsRequired;

  return (
    <section className="detail">
      <header className="pagehead">
        <a className="link back" href={href({ name: 'deposits' })}>
          ‹ Queue
        </a>
        <h1>{etb(deposit.amount)}</h1>
        <Status value={deposit.status} />
      </header>

      <ErrorBanner error={error} />

      {/*
        A claim that disagrees with its Telebirr SMS. Ranked alongside the
        reuse warning rather than buried in the review note, because the <h1>
        above shows the *claimed* figure and a reviewer scanning quickly would
        otherwise carry the wrong number into their decision. Only the SMS
        amount is ever credited — `approve()`'s settledAmount — so this is a
        discrepancy to understand, not an error to correct: the claim is kept
        verbatim on purpose, as the record of what the player asserted.
      */}
      {deposit.smsAmount !== null && deposit.smsAmount !== deposit.amount ? (
        <Banner kind="warn">
          <strong>
            Claimed {etb(deposit.amount)}, but the Telebirr SMS confirms{' '}
            {etb(deposit.smsAmount)}.
          </strong>{' '}
          {etb(deposit.smsAmount)} is what gets credited — the claimed figure is kept only as a
          record of what the player entered. A large over-claim is worth a look before approving.
        </Banner>
      ) : null}

      {/*
        The reuse warning sits above everything, including the image. It is the
        one fact on this page the reviewer cannot work out for themselves by
        looking carefully — the receipt will look perfectly genuine, because it
        is: it is just the same genuine receipt as an earlier claim.
      */}
      {deposit.proofReuseCount > 0 ? (
        <Banner kind="warn">
          <strong>This image has already been used on {deposit.proofReuseCount} earlier{' '}
          {deposit.proofReuseCount === 1 ? 'deposit' : 'deposits'}.</strong> The same transfer may be
          being claimed twice under different references. Check the bank statement before approving.
        </Banner>
      ) : null}

      {/*
        Only relevant while a decision is still pending — an already-decided
        deposit (approved outright by a Telebirr SMS match, most commonly)
        has nothing left to "approve", so a nudge about approving on other
        evidence is just noise once `decided` is true.
      */}
      {deposit.proofId === null && !decided ? (
        <Banner kind="warn">
          No receipt was attached. Approving is still possible on other evidence — the claim is
          recorded either way — but there is nothing here to check it against.
        </Banner>
      ) : null}

      <div className="detail__grid">
        <div className="detail__facts">
          <Row label="Reference">
            <code>{deposit.txReference}</code>
          </Row>
          <Row label="Method">{deposit.method}</Row>
          <Row label="Player">
            <Id value={deposit.userId} />
          </Row>
          <Row label="Submitted">
            {at(deposit.createdAt)} <span className="muted">({since(deposit.createdAt)})</span>
          </Row>
          {decided ? (
            <>
              <Row label="Decided">{at(deposit.reviewedAt)}</Row>
              <Row label="By">
                <Id value={deposit.reviewedBy} />
              </Row>
              {deposit.reviewNote ? <Row label="Note">{deposit.reviewNote}</Row> : null}
              <Row label="Ledger txn">
                <Id value={deposit.ledgerTransactionId} />
              </Row>
            </>
          ) : null}
        </div>

        <Proof deposit={deposit} onRefresh={load} />
      </div>

      <Approvals
        deposit={deposit}
        me={me}
        iApproved={iApproved}
        needsAnother={needsAnother}
      />

      {!decided ? (
        <div className="actions">
          {deposit.status === 'submitted' ? (
            <button
              type="button"
              className="button"
              disabled={busy || !can(me.role, 'deposit.review')}
              onClick={() => void act(() => api.startDepositReview(deposit.id))}
            >
              Start review
            </button>
          ) : null}

          {deposit.smsAmount === null && deposit.method === 'telebirr' ? (
            <div className="card">
              <h2>No Telebirr SMS for this reference</h2>
              <p className="muted">
                The claimed amount is typed by the player, so it cannot be credited on its own.
                Check the Telebirr statement and enter what actually arrived — or wait for the SMS,
                or reject.
              </p>
              <Field label="Amount you verified (ETB)">
                <input
                  className="input"
                  inputMode="decimal"
                  placeholder={etb(deposit.amount)}
                  value={verified}
                  onChange={(e) => setVerified(e.target.value)}
                />
              </Field>
            </div>
          ) : null}

          <ConfirmButton
            className="button button--primary"
            confirm={
              deposit.approvalsRequired > 1 && approvals.length + (iApproved ? 0 : 1) < deposit.approvalsRequired
                ? 'Record your approval? A second admin will still be needed.'
                : verified.trim()
                  ? `Credit the player ${verified.trim()} ETB, the amount you verified?`
                  : 'Approve and credit the player?'
            }
            disabled={busy || !can(me.role, 'deposit.approve') || iApproved}
            onConfirm={() =>
              void act(() => {
                const parsed = verified.trim() ? parseAmount(verified.trim()) : null;
                if (parsed && !parsed.ok) {
                  throw new Error('Enter a valid amount, for example 20 or 20.50');
                }
                return api.approveDeposit(
                  deposit.id,
                  undefined,
                  parsed && parsed.ok ? parsed.santim : undefined,
                );
              })
            }
          >
            {iApproved ? 'You have approved' : 'Approve'}
          </ConfirmButton>

          <ReasonForm
            action="Reject"
            placeholder="Why is this being rejected?"
            danger
            disabled={busy || !can(me.role, 'deposit.reject')}
            onSubmit={(reason) => void act(() => api.rejectDeposit(deposit.id, reason))}
          />
        </div>
      ) : null}

      {audit ? <AuditTrail entries={audit} /> : null}
    </section>
  );
}

/**
 * The receipt.
 *
 * `proofUrl` is minted per response and expires in minutes (§7.9), so this
 * handles the case that actually happens: a reviewer leaves the tab open,
 * comes back, and the image is a broken icon. Re-fetching the deposit mints a
 * fresh link, which is why the recovery is "reload" rather than "sign in
 * again".
 */
function Proof({
  deposit,
  onRefresh,
}: {
  deposit: AdminDepositDetailView;
  onRefresh: () => Promise<void>;
}) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [deposit.proofUrl]);

  if (!deposit.proofUrl) return <div className="proof proof--none">No receipt</div>;

  if (failed) {
    return (
      <div className="proof proof--none">
        {/*
          Phrased as a possibility rather than a diagnosis: the browser reports
          only that the load failed, and an expired signature and an unreachable
          API look identical from here. Reloading fixes the first and is
          harmless for the second.
        */}
        <p>This image could not be loaded. The link may have expired.</p>
        <button type="button" className="button" onClick={() => void onRefresh()}>
          Reload
        </button>
      </div>
    );
  }

  return (
    <figure className="proof">
      {/*
        Wrapped in a link so the full-size image opens in a new tab — a phone
        screenshot scaled into a column is not readable enough to check a
        reference number against. The API serves it `Content-Disposition:
        attachment`, which governs navigation and not this <img>, so following
        the link downloads rather than rendering it in this origin.
      */}
      <a href={apiUrl(deposit.proofUrl)} target="_blank" rel="noreferrer noopener">
        <img
          className="proof__image"
          src={apiUrl(deposit.proofUrl)}
          alt="Transfer receipt uploaded by the player"
          onError={() => setFailed(true)}
        />
      </a>
      <figcaption className="muted">Link valid until {at(deposit.proofUrlExpiresAt)}</figcaption>
    </figure>
  );
}

/**
 * Two-person review, made visible.
 *
 * Showing *who* approved is the point, not decoration: a second reviewer has to
 * be able to tell an independent opinion from their own earlier click, and the
 * control's guarantee — that one admin cannot be both halves — is only useful
 * if the person at the screen can see it holding.
 */
function Approvals({
  deposit,
  me,
  iApproved,
  needsAnother,
}: {
  deposit: AdminDepositDetailView;
  me: AdminView;
  iApproved: boolean;
  needsAnother: boolean;
}) {
  if (deposit.approvalsRequired === 1 && deposit.approvals.length === 0) return null;

  return (
    <div className="approvals">
      <h2>
        Approvals <span className="muted">{deposit.approvals.length} of {deposit.approvalsRequired}</span>
      </h2>

      {deposit.approvalsRequired > 1 ? (
        <p className="muted">
          This deposit is at or above the two-person review threshold, so it credits nobody until two
          different admins have approved it.
        </p>
      ) : null}

      <ul className="approvals__list">
        {deposit.approvals.map((a) => (
          <li key={a.adminId}>
            ✓ <Id value={a.adminId} />
            {a.adminId === me.id ? <span className="muted"> (you)</span> : null}
            <span className="muted"> · {at(a.createdAt)}</span>
            {a.note ? <span> — {a.note}</span> : null}
          </li>
        ))}
      </ul>

      {needsAnother ? (
        <Banner kind="warn">
          {iApproved
            ? 'Your approval is recorded. Nothing has been credited — a different admin must approve as well.'
            : 'One admin has approved. Yours would be the second, and would credit the player.'}
        </Banner>
      ) : null}
    </div>
  );
}

function AuditTrail({ entries }: { entries: AuditEntry[] }) {
  return (
    <details className="audit">
      <summary>Audit trail ({entries.length})</summary>
      <ul>
        {entries.map((e) => (
          <li key={e.id}>
            <code>{e.action}</code> <span className="muted">· {at(e.createdAt)} ·</span>{' '}
            {e.actorType === 'admin' ? <Id value={e.actorId} /> : <span>{e.actorType}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}
